import { supabase } from '../supabase';
import type { GroqMessage } from './groqService';

export { AL_STREAMS, buildSystemPrompt } from './groqService';

const MODEL = process.env.REACT_APP_NVIDIA_MODEL || 'moonshotai/kimi-k2.5';

function resolveModel(modelOverride?: string): string {
    return modelOverride || MODEL;
}

function formatHistory(messages: { role: 'user' | 'model'; text: string }[]): GroqMessage[] {
    return messages.map((msg) => ({
        role: msg.role === 'model' ? ('assistant' as const) : ('user' as const),
        content: msg.text,
    }));
}

type NvidiaChatResponse = {
    error?: string;
    choices?: Array<{
        message?: {
            content?: string;
            reasoning?: string;
            reasoning_content?: string;
        };
    }>;
};

async function invokeNvidiaChat(
    messages: GroqMessage[],
    modelOverride?: string
): Promise<string> {
    const { data, error } = await supabase.functions.invoke('ai-chat', {
        body: {
            messages,
            provider: 'nvidia',
            model: resolveModel(modelOverride),
            stream: false,
        },
    });

    if (error) {
        throw new Error(
            'Unable to reach the AI function. Make sure the `ai-chat` function is active and the `NVIDIA_API_KEY` secret is set in Supabase.'
        );
    }

    const result = data as NvidiaChatResponse | null;
    const message = result?.choices?.[0]?.message;
    const text = [message?.content, message?.reasoning_content, message?.reasoning]
        .filter(Boolean)
        .join('\n\n');

    if (!text) {
        throw new Error(result?.error || 'No response received from the AI. Please try again.');
    }

    return text;
}

export async function sendMessageStream(
    history: { role: 'user' | 'model'; text: string }[],
    userMessage: string,
    systemPrompt: string,
    onChunk: (accumulatedText: string) => void,
    modelOverride?: string
): Promise<string> {
    const recentHistory = history.slice(-40);
    const messages: GroqMessage[] = [
        { role: 'system', content: systemPrompt },
        ...formatHistory(recentHistory),
        { role: 'user', content: userMessage },
    ];

    const fullText = await invokeNvidiaChat(messages, modelOverride);
    onChunk(fullText);
    return fullText;
}

export async function sendMessage(
    history: { role: 'user' | 'model'; text: string }[],
    userMessage: string,
    systemPrompt: string,
    modelOverride?: string
): Promise<string> {
    const recentHistory = history.slice(-40);
    const messages: GroqMessage[] = [
        { role: 'system', content: systemPrompt },
        ...formatHistory(recentHistory),
        { role: 'user', content: userMessage },
    ];

    return invokeNvidiaChat(messages, modelOverride);
}
